import { Card } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Clock, Calendar, Sun, Moon, Sunset } from "lucide-react"

const DAYS = [
  { id: "sunday", label: "Sunday" },
  { id: "monday", label: "Monday" },
  { id: "tuesday", label: "Tuesday" },
  { id: "wednesday", label: "Wednesday" },
  { id: "thursday", label: "Thursday" },
  { id: "friday", label: "Friday" },
  { id: "saturday", label: "Saturday" },
]

const TimeSlotBadge = ({ icon: Icon, label, time, variant }) => {
  if (!time) return null

  return (
    <div className="flex items-center gap-2">
      <Icon className="h-3 w-3 text-gray-500" />
      <Badge variant={variant} className="font-medium">
        {label}: {time}
      </Badge>
    </div>
  )
}

export function ScheduleDisplay({ schedule }) {
  if (!schedule) return null

  const hasSchedule = DAYS.some((day) => schedule[day.id])

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 pb-4 border-b">
        <div
          className={`p-2 sm:p-3 rounded-full ${
            schedule.is_oncall ? "bg-green-100" : "bg-blue-100"
          }`}
        >
          <Clock
            className={`h-4 w-4 sm:h-5 sm:w-5 ${
              schedule.is_oncall ? "text-green-600" : "text-blue-600"
            }`}
          />
        </div>
        <div>
          <h3 className="text-sm sm:text-base font-medium">
            Availability Status
          </h3>
          <Badge
            variant={schedule.is_oncall ? "success" : "secondary"}
            className="mt-1 text-xs sm:text-sm"
          >
            {schedule.is_oncall ? "On Call" : "Regular Schedule"}
          </Badge>
        </div>
      </div>

      {hasSchedule ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-1">
          {DAYS.map(({ id, label }) => {
            const daySchedule = schedule[id]
            if (!daySchedule) return null

            return (
              <Card key={id} className="p-4 hover:shadow-md transition-shadow">
                <div className="space-y-4">
                  <div className="flex items-center gap-2 pb-3 border-b">
                    <Calendar className="h-4 w-4 text-blue-600" />
                    <span className="font-semibold text-gray-900">{label}</span>
                  </div>

                  <div className="grid gap-2 pl-6">
                    <TimeSlotBadge
                      icon={Sun}
                      label="Morning"
                      time={daySchedule.morning}
                      variant="outline"
                    />
                    <TimeSlotBadge
                      icon={Sunset}
                      label="Afternoon"
                      time={daySchedule.afternoon}
                      variant="outline"
                    />
                    <TimeSlotBadge
                      icon={Moon}
                      label="Evening"
                      time={daySchedule.evening}
                      variant="outline"
                    />
                  </div>
                </div>
              </Card>
            )
          })}
        </div>
      ) : (
        <Card className="p-6 sm:p-8">
          <div className="text-center space-y-3">
            <Calendar className="h-10 w-10 sm:h-12 sm:w-12 mx-auto text-gray-400" />
            <h3 className="text-base sm:text-lg font-medium text-gray-900">
              No Schedule Set
            </h3>
            <p className="text-xs sm:text-sm text-gray-500">
              {schedule.is_oncall
                ? "The doctor is available on call."
                : "No regular schedule has been set for this doctor."}
            </p>
          </div>
        </Card>
      )}
    </div>
  )
}
